import React, { useState } from 'react';
import { HIGHLIGHTERS, Highlighter, claimFirstAppearance } from '../../lib/paper';
import { LibraryStats } from '../../lib/account';
import { Tape } from '../paper';

const Stat: React.FC<{ value: number | null; label: string }> = ({ value, label }) => (
  <div className="flex-1">
    <p className="m-0 font-headline text-2xl font-medium italic leading-7 text-on-surface">{value ?? '—'}</p>
    <p className="m-0 font-body text-[10px] font-semibold uppercase tracking-[0.1em] text-on-surface-variant">{label}</p>
  </div>
);

/**
 * The Account header as a library "access card" (mobile's LibraryCard): ruled
 * index card with a monogram, card number, notebook stats and a rubber stamp
 * for the plan.
 */
export const LibraryCard: React.FC<{
  name: string;
  email: string;
  monogram: string;
  cardNumber: string;
  memberSince: string;
  stamp: { label: string; tone: Highlighter };
  stats: LibraryStats;
}> = ({ name, email, monogram, cardNumber, memberSince, stamp, stats }) => {
  const [firstStamp] = useState(() => claimFirstAppearance('account:stamp'));
  return (
    <div className="pb-card relative mt-4 -rotate-[0.6deg] rounded-lg px-4 pb-4 pt-5 md:px-6 md:pt-6">
      <Tape tone={HIGHLIGHTERS.sky} width={70} height={20} rotate={-6} className="left-5 top-[-9px]" />
      <div className="flex items-baseline justify-between">
        <span className="font-body text-[10px] font-bold uppercase tracking-[0.2em] text-accent">Access Card</span>
        <span className="font-body text-[11px] font-semibold tracking-[0.1em] text-on-surface-variant">No. {cardNumber}</span>
      </div>
      {/* The red rule an index card has under its header line. */}
      <div className="mt-2 h-[1.5px] bg-[rgba(142,50,22,0.4)]" />

      <div className="mt-4 flex items-center gap-3">
        <span className="flex h-[52px] w-[52px] shrink-0 -rotate-[4deg] items-center justify-center rounded-full bg-hl-lilac font-headline text-[22px] font-medium italic text-hl-lilac-ink">
          {monogram}
        </span>
        <div className="min-w-0 flex-1">
          <p className="m-0 truncate font-headline text-[26px] font-medium italic leading-[30px] text-on-surface">{name}</p>
          <p className="m-0 truncate font-body text-[0.8125rem] text-on-surface-variant">{email}</p>
        </div>
      </div>

      <div className="mt-4 flex border-0 border-y border-solid border-[rgba(27,76,120,0.14)] py-2.5">
        <Stat value={stats.pages} label="pages" />
        <Stat value={stats.notebooks} label="notebooks" />
        <Stat value={stats.tags} label="tags" />
      </div>

      <div className="mt-3 flex items-center justify-between">
        <span className="flex-1 font-headline text-[0.8125rem] italic text-on-surface-variant">Member since {memberSince}</span>
        <span
          aria-label={`Subscription ${stamp.label}`}
          className={`mr-1 flex -rotate-[14deg] flex-col items-center rounded-xl border-2 border-solid px-2.5 py-1 ${firstStamp ? 'pb-stamp' : ''}`}
          style={{ borderColor: stamp.tone.ink, backgroundColor: stamp.tone.fill, color: stamp.tone.ink }}
        >
          <span className="font-body text-[8px] font-bold uppercase tracking-[0.19em]">Plan</span>
          <span className="font-body text-sm font-bold uppercase tracking-[0.14em]">{stamp.label}</span>
        </span>
      </div>
    </div>
  );
};
